import api, { API_BASE } from './api';

// --- AI intelligence (ROADMAP.md M3) ---

// Ordered transcript events from a single assistant turn. The AI service emits
// tool_call (progress), artifact (data the frontend charts), and text events.
export type ChatEvent =
  | { type: 'text'; content: string }
  | { type: 'tool_call'; name: string; args?: Record<string, unknown> }
  | { type: 'artifact'; kind: string; data: unknown };

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;            // text answer (also what we send back as history)
  events?: ChatEvent[];       // assistant turns only: the rendered transcript
}

export interface ConversationSummary {
  id: number;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface ConversationDetail extends ConversationSummary {
  messages: { id: number; role: 'user' | 'assistant'; content: string; events: ChatEvent[] }[];
}

export interface ChatResponse {
  answer: string;
  model: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens: number;
    cache_creation_input_tokens: number;
  };
  events: ChatEvent[];
}

// Streamed events (superset of ChatEvent): tool_call carries an id and is emitted
// before execution; tool_result marks it done; done/error are terminal control
// events. See services/ai conversation.stream_events.
export type StreamEvent =
  | { type: 'text'; content: string }
  | { type: 'tool_call'; id: string; name: string; args?: Record<string, unknown> }
  | { type: 'tool_result'; id: string; ok: boolean }
  | { type: 'artifact'; id: string; kind: string; data: unknown }
  | { type: 'done'; model: string; answer: string; usage: ChatResponse['usage'] }
  | { type: 'error'; message: string };

// POST the question and stream the AI service's newline-delimited JSON events,
// invoking onEvent for each. Uses fetch (not axios) for the ReadableStream body.
export async function streamPortfolioQuestion(
  portfolioId: number,
  question: string,
  history: ChatTurn[],
  currency: string,
  onEvent: (ev: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/portfolios/${portfolioId}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ question, history: history.map((t) => ({ role: t.role, content: t.content })), currency }),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Chat stream failed (${res.status})`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line) onEvent(JSON.parse(line) as StreamEvent);
    }
  }
  const tail = buffer.trim();
  if (tail) onEvent(JSON.parse(tail) as StreamEvent);
}

export interface ETF {
  id: number;
  ticker: string;
  name: string;
  provider: {
    id: number;
    name: string;
  };
  last_updated_at: string;
  metadata: Record<string, unknown>;
}

export interface ETFHolding {
  id: number;
  asset_identifier: string;
  asset_name: string;
  asset_type: string;
  weight: number;
  industry: string;
  country: string;
  as_of_date: string;
}

export interface Asset {
  id: number;
  identifier: string;
  name: string;
  asset_type: string;
  country?: string;
  industry?: string;
}

export interface Portfolio {
  id: number;
  name: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  total_value: number;
  last_etf_sync_at: string | null;
  entries: PortfolioEntry[];
  asset_entries: PortfolioAssetEntry[];
}

export interface PortfolioEntry {
  id: number;
  portfolio_id: number;
  etf_id: number;
  etf: ETF;
  shares: number;
  purchase_price: number;
  purchase_date: string;
  current_price: number;
  current_value: number;
}

export interface PortfolioAssetEntry {
  id: number;
  portfolio_id: number;
  asset_id: number;
  asset: Asset;
  shares: number;
  purchase_price: number;
  purchase_date: string;
  current_price: number;
  current_value: number;
  weight_in_portfolio: number;
}

export interface CreatePortfolioParams {
  name: string;
}

export interface AddPortfolioEntryParams {
  etf_id: number;
  shares: number;
  purchase_price?: number;
  purchase_date?: string;
  purchase_currency?: string;
}

export interface AddPortfolioAssetEntryParams {
  asset_id: number;
  shares: number;
  purchase_price?: number;
  purchase_date?: string;
  purchase_currency?: string;
}

export interface PortfolioHoldingsBreakdown {
  industries: Record<string, number>;
  countries: Record<string, number>;
  stocks: Record<string, number>;
}

export interface SecurityExposure {
  identifier: string;
  name: string;
  security_type: string;
  total_shares: number;
  total_value: number;
  direct_shares: number;
  direct_value: number;
  etf_shares: number;
  etf_value: number;
}

export interface TotalExposureData {
  securities: SecurityExposure[];
  total_portfolio_value: number;
}

export interface PerformancePosition {
  type: 'etf' | 'asset';
  id: number;
  name: string;
  ticker: string;
  shares: number;
  purchase_price: number | null;
  purchase_date: string | null;
  cost_basis: number | null;
  current_price: number;
  current_value: number;
  absolute_gain: number | null;
  return_pct: number | null;
  day_change_pct: number | null;
}

export interface PerformanceSummary {
  cost_basis: number | null;
  current_value: number;
  absolute_gain: number | null;
  return_pct: number | null;
}

export interface PerformanceData {
  summary: PerformanceSummary;
  positions: PerformancePosition[];
  series: { date: string; value: number }[];
  from: string;
  to: string;
}

export interface IsinLookupResult {
  type: 'etf' | 'asset';
  id: number;
  ticker: string;
  name: string;
  asset_type?: string;
  found_in_db: boolean;
}

export interface BenchmarkHistory {
  ticker: string;
  series: { date: string; price: number }[];
}

export interface MetricValue {
  value: number | null;
  coverage: number;
  n: number;
}

export interface ValuationUniverse {
  securities_total: number;
  equity_total: number;
  equity_considered: number;
  min_weight_threshold: number;
  capped: boolean;
  equity_weight: number;
  resolved: number;
  resolved_weight: number;
  unresolved_sample: string[];
}

export interface SectorSecurity {
  identifier: string;
  name: string | null;
  weight: number;
  value: number;
  pe: number | null;
  pb: number | null;
}

export interface SectorBreakdownRow {
  sector: string;
  equity_weight: number;
  value: number;
  pe: MetricValue;
  pb: MetricValue;
  securities: SectorSecurity[];
}

export interface MarketCapDistribution {
  large_cap_weight: number;
  mid_cap_weight: number;
  small_cap_weight: number;
  covered_weight: number;
}

export interface ValuationData {
  source: string;
  as_of: string;
  metrics: {
    blended_pe: MetricValue;
    forward_pe: MetricValue;
    blended_pb: MetricValue;
    peg_ratio: MetricValue;
    ev_to_ebitda: MetricValue;
    dividend_yield: MetricValue;
    return_on_equity: MetricValue;
    profit_margin: MetricValue;
    debt_to_equity: MetricValue;
  };
  universe: ValuationUniverse;
  sector_breakdown: SectorBreakdownRow[];
  market_cap: MarketCapDistribution;
}

export interface WindowRisk {
  window_years: number;
  days_used: number;
  annualized_return: number | null;
  volatility: number | null;
  beta: number | null;
  correlation: number | null;
  r_squared: number | null;
  sharpe: number | null;
  sortino: number | null;
  max_drawdown: number | null;
  current_drawdown: number | null;
  var_95: number | null;
  var_99: number | null;
  es_95: number | null;
  es_99: number | null;
}

export interface RiskCoverage {
  positions_total: number;
  positions_covered: number;
  weight_covered: number;
  benchmark_days: number;
}

export interface ScatterPoint {
  volatility: number;
  annualized_return: number;
}

export interface InstrumentPoint extends ScatterPoint {
  identifier: string;
  name: string | null;
  weight: number;
}

export interface FrontierPoint {
  volatility: number;
  ret: number;
  sharpe: number;
}

export interface Frontier {
  cloud: FrontierPoint[];
  curve: FrontierPoint[];   // analytic long-only efficient frontier
  max_sharpe: FrontierPoint;
  min_variance: FrontierPoint;
}

export interface RiskCharts {
  window_years: number;
  risk_free_rate: number;
  instruments: InstrumentPoint[];
  portfolio: ScatterPoint;
  benchmark: ScatterPoint | null;
  frontier: Frontier | null;
  correlation_labels: string[];
  correlation_matrix: number[][];
  dates: string[];
  cumulative_portfolio: number[];
  cumulative_benchmark: number[] | null;
  drawdown: number[];
  daily_returns: number[];
}

export interface RiskAttributionPosition {
  identifier: string;
  name: string | null;
  weight: number;                   // covered-normalized weight (sums to 1)
  volatility: number;               // standalone annualized vol
  correlation_to_portfolio: number;
  marginal_contribution: number;
  risk_contribution: number;        // sums to portfolio_volatility
  percent_of_risk: number;          // sums to 1
  risk_to_weight: number | null;    // >1 = punches above its weight
}

export interface RiskAttribution {
  window_years: number;
  portfolio_volatility: number;
  weighted_avg_volatility: number;
  diversification_ratio: number | null;
  weight_covered: number;
  positions: RiskAttributionPosition[];
}

export interface RiskData {
  as_of: string;
  benchmark: string;
  risk_free_rate: number;
  coverage: RiskCoverage;
  windows: WindowRisk[];
  attribution: RiskAttribution | null;
  charts: RiskCharts | null;
  computed_at?: string;   // UTC ISO8601 — when the cached result was computed
}

export interface ETFOverlapEntry {
  identifier: string;
  name: string;
  industry?: string;
  country?: string;
  combined_weight: number;
  etfs: {
    etf_id: number;
    ticker: string;
    name: string;
    weight_in_etf: number;
    etf_portfolio_weight: number;
  }[];
}

export interface ETFSimilarityETF {
  id: number;
  ticker: string;
  name: string;
}

export interface ETFHoldingEntry {
  identifier: string;
  name: string;
  weight: number;
}

export interface ETFOverlapData {
  overlapping: ETFOverlapEntry[];
  etf_count: number;
  etfs: ETFSimilarityETF[];
  similarity_matrix: number[][];
  holdings_by_etf: Record<string, ETFHoldingEntry[]>;
  total_etf_value: number;
}

/**
 * Helper function to adapt backend response format
 * Maps portfolio_entries to entries and portfolio_asset_entries to asset_entries if needed
 */
const adaptPortfolioResponse = (data: any): Portfolio => {
  let result: any = { ...data };
  
  // Ensure total_value is a number, default to 0 if not present
  result.total_value = data.total_value ? Number(data.total_value) : 0;
  
  // Handle portfolio entries (ETFs)
  if (data.portfolio_entries && !data.entries) {
    // Process portfolio entries to ensure correct data types
    const normalizedEntries = data.portfolio_entries.map((entry: any) => ({
      ...entry,
      shares: typeof entry.shares === 'number' ? entry.shares : Number(entry.shares),
      purchase_price: entry.purchase_price 
        ? (typeof entry.purchase_price === 'number' 
          ? entry.purchase_price 
          : Number(entry.purchase_price))
        : null,
      current_price: entry.current_price 
        ? (typeof entry.current_price === 'number' 
          ? entry.current_price 
          : Number(entry.current_price))
        : null,
      current_value: entry.current_value 
        ? (typeof entry.current_value === 'number' 
          ? entry.current_value 
          : Number(entry.current_value))
        : null
    }));
    
    result.entries = normalizedEntries;
  } else if (data.entries) {
    // If entries exist but may need type normalization
    const normalizedEntries = data.entries.map((entry: any) => ({
      ...entry,
      shares: typeof entry.shares === 'number' ? entry.shares : Number(entry.shares),
      purchase_price: entry.purchase_price 
        ? (typeof entry.purchase_price === 'number' 
          ? entry.purchase_price 
          : Number(entry.purchase_price))
        : null,
      current_price: entry.current_price 
        ? (typeof entry.current_price === 'number' 
          ? entry.current_price 
          : Number(entry.current_price))
        : null,
      current_value: entry.current_value 
        ? (typeof entry.current_value === 'number' 
          ? entry.current_value 
          : Number(entry.current_value))
        : null
    }));
    
    result.entries = normalizedEntries;
  } else {
    // If entries is undefined, ensure it's at least an empty array
    result.entries = [];
  }
  
  // Handle asset entries (direct investments)
  if (data.portfolio_asset_entries && !data.asset_entries) {
    // Process asset entries to ensure correct data types
    const normalizedAssetEntries = data.portfolio_asset_entries.map((entry: any) => ({
      ...entry,
      shares: typeof entry.shares === 'number' ? entry.shares : Number(entry.shares),
      purchase_price: entry.purchase_price 
        ? (typeof entry.purchase_price === 'number' 
          ? entry.purchase_price 
          : Number(entry.purchase_price))
        : null,
      current_price: entry.current_price 
        ? (typeof entry.current_price === 'number' 
          ? entry.current_price 
          : Number(entry.current_price))
        : null,
      current_value: entry.current_value 
        ? (typeof entry.current_value === 'number' 
          ? entry.current_value 
          : Number(entry.current_value))
        : null,
      weight_in_portfolio: entry.weight_in_portfolio 
        ? (typeof entry.weight_in_portfolio === 'number' 
          ? entry.weight_in_portfolio 
          : Number(entry.weight_in_portfolio))
        : 0
    }));
    
    result.asset_entries = normalizedAssetEntries;
  } else if (data.asset_entries) {
    // If asset entries exist but may need type normalization
    const normalizedAssetEntries = data.asset_entries.map((entry: any) => ({
      ...entry,
      shares: typeof entry.shares === 'number' ? entry.shares : Number(entry.shares),
      purchase_price: entry.purchase_price 
        ? (typeof entry.purchase_price === 'number' 
          ? entry.purchase_price 
          : Number(entry.purchase_price))
        : null,
      current_price: entry.current_price 
        ? (typeof entry.current_price === 'number' 
          ? entry.current_price 
          : Number(entry.current_price))
        : null,
      current_value: entry.current_value 
        ? (typeof entry.current_value === 'number' 
          ? entry.current_value 
          : Number(entry.current_value))
        : null,
      weight_in_portfolio: entry.weight_in_portfolio 
        ? (typeof entry.weight_in_portfolio === 'number' 
          ? entry.weight_in_portfolio 
          : Number(entry.weight_in_portfolio))
        : 0
    }));
    
    result.asset_entries = normalizedAssetEntries;
  } else {
    // If asset_entries is undefined, ensure it's at least an empty array
    result.asset_entries = [];
  }
  
  return result as Portfolio;
};

/**
 * Portfolio service for managing user portfolios and ETFs
 */
const portfolioService = {
  /**
   * Get all portfolios for the current user
   */
  getPortfolios: async (): Promise<Portfolio[]> => {
    const response = await api.get('/users/current/portfolios');
    return Array.isArray(response.data) 
      ? response.data.map(adaptPortfolioResponse)
      : [];
  },

  /**
   * Get a specific portfolio by ID
   */
  getPortfolio: async (id: number): Promise<Portfolio> => {
    const response = await api.get(`/portfolios/${id}`);
    return adaptPortfolioResponse(response.data);
  },

  /**
   * Create a new portfolio
   */
  createPortfolio: async (data: CreatePortfolioParams): Promise<Portfolio> => {
    const response = await api.post('/portfolios', { portfolio: data });
    return adaptPortfolioResponse(response.data);
  },

  /**
   * Update a portfolio
   */
  updatePortfolio: async (id: number, data: Partial<CreatePortfolioParams>): Promise<Portfolio> => {
    const response = await api.put(`/portfolios/${id}`, { portfolio: data });
    return adaptPortfolioResponse(response.data);
  },

  /**
   * Delete a portfolio
   */
  deletePortfolio: async (id: number): Promise<void> => {
    await api.delete(`/portfolios/${id}`);
  },

  /**
   * Add an ETF to a portfolio
   */
  addPortfolioEntry: async (portfolioId: number, data: AddPortfolioEntryParams): Promise<PortfolioEntry> => {
    const response = await api.post(`/portfolios/${portfolioId}/entries`, { entry: data });
    return response.data;
  },

  /**
   * Update a portfolio entry
   */
  updatePortfolioEntry: async (
    portfolioId: number, 
    entryId: number, 
    data: Partial<AddPortfolioEntryParams>
  ): Promise<PortfolioEntry> => {
    const response = await api.put(`/portfolios/${portfolioId}/entries/${entryId}`, { entry: data });
    return response.data;
  },

  /**
   * Remove an ETF from a portfolio
   */
  removePortfolioEntry: async (portfolioId: number, entryId: number): Promise<void> => {
    await api.delete(`/portfolios/${portfolioId}/entries/${entryId}`);
  },

  /**
   * Get all available ETFs
   */
  getETFs: async (): Promise<ETF[]> => {
    const response = await api.get('/etfs');
    return response.data;
  },

  /**
   * Get a specific ETF by ID
   */
  getETF: async (id: number): Promise<ETF> => {
    try {
      const response = await api.get(`/etfs/${id}`);
      
      // Check if the ETF is nested in an 'etf' field (API returns {etf: {...}, metadata: {...}, last_updated_at: '...'})
      if (response.data && response.data.etf) {
        // The response has a nested ETF object format, so extract and return the ETF with the metadata
        return {
          ...response.data.etf,
          metadata: response.data.metadata || {},
          last_updated_at: response.data.last_updated_at
        };
      }
      
      // Return as-is if not nested
      return response.data;
    } catch (error) {
      console.error('Error fetching ETF details:', error);
      throw error; // Re-throw to allow React Query to handle the error
    }
  },

  /**
   * Get holdings for a specific ETF
   */
  getETFHoldings: async (etfId: number): Promise<ETFHolding[]> => {
    try {
      const response = await api.get(`/etfs/${etfId}/holdings`);
      
      // Handle different response formats
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.holdings && Array.isArray(response.data.holdings)) {
        return response.data.holdings;
      } else if (response.data && response.data.meta && response.data.holdings && Array.isArray(response.data.holdings)) {
        return response.data.holdings;
      } else {
        console.error('Unexpected holdings response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching ETF holdings:', error);
      return [];
    }
  },

  /**
   * Get holdings breakdown for a portfolio
   */
  getPortfolioHoldingsBreakdown: async (portfolioId: number): Promise<PortfolioHoldingsBreakdown> => {
    const response = await api.get(`/portfolios/${portfolioId}/holdings_breakdown`);
    return response.data;
  },

  /**
   * Get all assets available for direct investment
   */
  getAssets: async (): Promise<Asset[]> => {
    const response = await api.get('/assets');
    // Handle both response formats: {assets: Asset[]} or Asset[] directly
    return response.data.assets || response.data;
  },

  /**
   * Get a specific asset by ID
   */
  getAsset: async (id: number): Promise<Asset> => {
    try {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching asset details:', error);
      throw error;
    }
  },

  /**
   * Get all direct asset investments for a portfolio
   */
  getPortfolioAssetEntries: async (portfolioId: number): Promise<PortfolioAssetEntry[]> => {
    const response = await api.get(`/portfolios/${portfolioId}/asset_entries`);
    return response.data;
  },

  /**
   * Add a direct asset investment to a portfolio
   */
  addPortfolioAssetEntry: async (portfolioId: number, data: AddPortfolioAssetEntryParams): Promise<PortfolioAssetEntry> => {
    const response = await api.post(`/portfolios/${portfolioId}/asset_entries`, { portfolio_asset_entry: data });
    return response.data;
  },

  /**
   * Update a direct asset investment
   */
  updatePortfolioAssetEntry: async (
    portfolioId: number, 
    entryId: number, 
    data: Partial<AddPortfolioAssetEntryParams>
  ): Promise<PortfolioAssetEntry> => {
    const response = await api.put(`/portfolios/${portfolioId}/asset_entries/${entryId}`, { portfolio_asset_entry: data });
    return response.data;
  },

  /**
   * Remove a direct asset investment from a portfolio
   */
  removePortfolioAssetEntry: async (portfolioId: number, entryId: number): Promise<void> => {
    await api.delete(`/portfolios/${portfolioId}/asset_entries/${entryId}`);
  },

  getPortfolioETFHoldingsBreakdown: async (portfolioId: number): Promise<PortfolioHoldingsBreakdown> => {
    const response = await api.get(`/portfolios/${portfolioId}/etf_holdings_breakdown`);
    return response.data;
  },

  getPortfolioAssetHoldingsBreakdown: async (portfolioId: number): Promise<PortfolioHoldingsBreakdown> => {
    const response = await api.get(`/portfolios/${portfolioId}/asset_holdings_breakdown`);
    return response.data;
  },

  /**
   * Get total exposure to individual securities (direct + via ETFs)
   */
  getPortfolioTotalExposure: async (portfolioId: number): Promise<TotalExposureData> => {
    try {
      const response = await api.get(`/portfolios/${portfolioId}/total_holdings_exposure`);
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio total exposure:', error);
      throw error;
    }
  },

  getPortfolioPerformance: async (portfolioId: number, from?: string, to?: string): Promise<PerformanceData> => {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await api.get(`/portfolios/${portfolioId}/performance`, { params });
    return response.data;
  },

  getPortfolioETFOverlap: async (portfolioId: number): Promise<ETFOverlapData> => {
    const response = await api.get(`/portfolios/${portfolioId}/etf_overlap`);
    return response.data;
  },


  resolveIsin: async (isin: string): Promise<IsinLookupResult> => {
    const response = await api.get('/resolve_isin', { params: { isin } });
    return response.data;
  },

  getPortfolioValuation: async (portfolioId: number): Promise<ValuationData> => {
    const response = await api.get(`/portfolios/${portfolioId}/valuation`);
    return response.data;
  },

  getPortfolioRisk: async (portfolioId: number): Promise<RiskData> => {
    const response = await api.get(`/portfolios/${portfolioId}/risk`);
    return response.data;
  },

  // Force a full risk recompute: busts the server cache (result + price series),
  // re-fetches from Yahoo and recomputes. Returns the fresh RiskData.
  recomputePortfolioRisk: async (portfolioId: number): Promise<RiskData> => {
    const response = await api.post(`/portfolios/${portfolioId}/risk/recompute`);
    return response.data;
  },

  // Conversational Q&A over the portfolio (ROADMAP.md M3). History is the prior
  // turns (oldest first), excluding the question being asked now.
  askPortfolioQuestion: async (
    portfolioId: number,
    question: string,
    history: ChatTurn[],
    currency?: string
  ): Promise<ChatResponse> => {
    const response = await api.post(`/portfolios/${portfolioId}/chat`, { question, history, currency });
    return response.data;
  },

  // --- Conversation persistence ---
  listConversations: async (portfolioId: number): Promise<ConversationSummary[]> =>
    (await api.get(`/portfolios/${portfolioId}/conversations`)).data,

  createConversation: async (portfolioId: number, title: string): Promise<ConversationSummary> =>
    (await api.post(`/portfolios/${portfolioId}/conversations`, { title })).data,

  getConversation: async (portfolioId: number, id: number): Promise<ConversationDetail> =>
    (await api.get(`/portfolios/${portfolioId}/conversations/${id}`)).data,

  renameConversation: async (portfolioId: number, id: number, title: string): Promise<ConversationSummary> =>
    (await api.patch(`/portfolios/${portfolioId}/conversations/${id}`, { title })).data,

  deleteConversation: async (portfolioId: number, id: number): Promise<void> => {
    await api.delete(`/portfolios/${portfolioId}/conversations/${id}`);
  },

  appendMessages: async (
    portfolioId: number,
    id: number,
    messages: { role: 'user' | 'assistant'; content: string; events?: ChatEvent[] }[],
  ): Promise<ConversationSummary> =>
    (await api.post(`/portfolios/${portfolioId}/conversations/${id}/messages`, { messages })).data,

  // Household memory (what Aumora remembers about the owner).
  getHouseholdMemory: async (portfolioId: number): Promise<Record<string, string>> => {
    const response = await api.get(`/portfolios/${portfolioId}/household_memory`);
    return response.data.memory || {};
  },

  // Merge facts into memory; a blank value forgets that key.
  updateHouseholdMemory: async (
    portfolioId: number,
    facts: Record<string, string>,
  ): Promise<Record<string, string>> => {
    const response = await api.post(`/portfolios/${portfolioId}/household_memory`, { facts });
    return response.data.memory || {};
  },

  getBenchmarkHistory: async (ticker: string, from?: string, to?: string): Promise<BenchmarkHistory> => {
    const params: Record<string, string> = { ticker };
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await api.get('/benchmarks/history', { params });
    return response.data;
  }
};

export default portfolioService; 